import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClientHelperService } from './http-client-helper.service';
import { RiskCatalog, RiskCatalogRequest, RiskCatalogsResponse, RiskRegistryFilters } from './models/risks/risks.interface';
import { JsonPatchRequest } from './models/assessments/assessment.interface';
import { HttpParams } from '@angular/common/http';
import { MineSort } from '../shared/mine-sort/mine-sort.interface';
import { SortDirectionEnum } from '../shared/sort-direction.enum';
import { Risk } from '../risks/models/risks.interface';
import { AssessmentEditRequest, AssessmentResourceId } from './models/assessments/assessment.interface';

@Injectable({
  providedIn: 'root'
})
export class BaseApiClientRisksService {
	protected readonly controller: string = 'risks';
  protected readonly riskController: string = 'assessment/v4/risk';

	constructor(protected httpClientHelper: HttpClientHelperService) { }

    getRisksCatalog(): Observable<RiskCatalogsResponse> {
        return this.httpClientHelper.invokeGetAuth<RiskCatalogsResponse>(`${this.controller}/catalog`);
    }

    createRiskCatalog(riskCatalog: RiskCatalogRequest): Observable<RiskCatalog> {
      return this.httpClientHelper.invokePostAuth<RiskCatalog>(`${this.controller}/catalog-item`, riskCatalog);
    }

    updateRiskCatalog(id: number, riskCatalog: RiskCatalogRequest): Observable<RiskCatalog> {
      return this.httpClientHelper.invokePutAuth<RiskCatalog>(`${this.controller}/catalog-item/${id}`, riskCatalog);
    }

    updateRiskCatalogByType(type: string, riskCatalog: RiskCatalogRequest): Observable<RiskCatalog> {
      return this.httpClientHelper.invokePutAuth<RiskCatalog>(`${this.controller}/catalog-item/type/${type}`, riskCatalog);
    }

    /** DevRiskLabels: JSON-patch an existing catalog row (e.g. `/isHidden`) without resending the whole item. */
    patchRiskCatalog(id: number, changes: JsonPatchRequest[]): Observable<RiskCatalog> {
      return this.httpClientHelper.invokePatchAuth<RiskCatalog>(`${this.controller}/catalog-item/${id}`, changes);
    }

    deleteRiskCatalog(id: number): Observable<void> {
      return this.httpClientHelper.invokeDeleteAuth<void>(`${this.controller}/catalog-item/${id}`);
    }

    getRiskRegistry(search = '', filters: RiskRegistryFilters = null, sort: MineSort = null): Observable<Risk[]> {
      return this.httpClientHelper.invokeGet<Risk[]>(`${this.riskController}`, this.getQueryParams(search, filters, sort));
    }

    createRisk(risk: Risk): Observable<Risk> {
      return this.httpClientHelper.invokePostAuth<Risk>(`${this.riskController}`, risk);
    }

    updateRisk(id: AssessmentResourceId, risks: AssessmentEditRequest[]): Observable<Risk> {
      return this.httpClientHelper.invokePatchAuth<Risk>(`${this.riskController}/${id}`, risks);
    }

    deleteRisk(id: AssessmentResourceId): Observable<void> {
      return this.httpClientHelper.invokeDeleteAuth<void>(`${this.riskController}/${id}`);
    }

    protected getQueryParams(search: string, filters: RiskRegistryFilters, sort: MineSort): HttpParams {
      let qParams = new HttpParams();
      if (!!search) {
        qParams = qParams.append('search.query', search);
      }

      qParams = qParams.appendAll({
        'filter.mitigations': filters?.mitigations ?? [] ,
        'filter.residualRisk': filters?.residualRisk ?? [],
        'filter.inherentRisk': filters?.inherentRisk ?? [],
        'filter.type': filters?.riskTypes ?? [],
        'relations.assessmentTypes': filters?.assessmentTypes ?? [],
        'relations.relatedEntityIds': filters?.dataSources ?? [],
      });

      const labels = filters?.labels ?? [];
      if (labels.length) {
        qParams = qParams.append('relations.tags', labels.join(','));
      }

      // DevRiskLabels: risk-owned labels (any-of), distinct from the assessment's `relations.tags`.
      const riskLabels = filters?.riskLabels ?? [];
      if (riskLabels.length) {
        qParams = qParams.append('relations.riskTags', riskLabels.join(','));
      }

      qParams = this.applySortParams(qParams, sort);

      return qParams;
    }

    private applySortParams(qParams: HttpParams, sort: MineSort): HttpParams {
      if (!!sort && sort?.direction !== SortDirectionEnum.Default) {
          qParams = qParams.append('orderBy.column', sort.active);
  
          if (sort.direction === SortDirectionEnum.Asc) {
              qParams = qParams.append('orderBy.descending', false);
          } else if (sort.direction === SortDirectionEnum.Desc) {
              qParams = qParams.append('orderBy.descending', true);
          }
      }
      return qParams;
  }
}
